import { AnyAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  ConversationWithRelations,
  ellipsisApi,
  DeleteMessagesByIdApiArg,
  Message,
  MessageWithRelations,
  PatchMessagesByIdApiArg,
  PostMessagesApiArg,
} from './ellipsisApi';

// Helper function to transform read receipts to use readDate instead of date or readAt
const transformReadReceipts = (message: any) => {
  if (message?.readReceipts?.length) {
    return {
      ...message,
      readReceipts: message.readReceipts.map((receipt: any) => {
        // Create a new receipt object with readDate
        const newReceipt = { ...receipt };

        // If date exists, use it for readDate and remove date
        if (newReceipt.date) {
          newReceipt.readDate = newReceipt.date;
          delete newReceipt.date;
        }

        // If readAt exists, use it for readDate and remove readAt
        if (newReceipt.readAt) {
          newReceipt.readDate = newReceipt.readAt;
          delete newReceipt.readAt;
        }

        return newReceipt;
      }),
    };
  }
  return message;
};

export type GetConversationsApiResponse = {
  conversations: ConversationWithRelations[];
  total: number;
};

export type GetConversationsApiArg = {
  archived?: string | boolean;
  unreadOnly?: boolean;
  scope?: 'mine' | 'all';
  limit?: number;
  skip?: number;
};

type ConversationsInitialPageParam = {
  skip: number;
  limit: number;
};

const extendedApi = ellipsisApi
  .enhanceEndpoints({
    addTagTypes: ['ConversationList'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getConversations: build.infiniteQuery<
        GetConversationsApiResponse,
        GetConversationsApiArg,
        ConversationsInitialPageParam
      >({
        infiniteQueryOptions: {
          initialPageParam: {
            skip: 0,
            limit: 10,
          },
          getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const nextOffset = lastPageParam.skip + lastPageParam.limit;
            const remainingItems = lastPage?.total
              ? lastPage?.total - nextOffset
              : 0;
            if (remainingItems <= 0) {
              return undefined;
            }

            return {
              ...lastPageParam,
              skip: nextOffset,
            };
          },
          getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
            const prevOffset = firstPageParam.skip - firstPageParam.limit;
            if (prevOffset < 0) return undefined;

            return {
              ...firstPageParam,
              offset: firstPageParam.skip - firstPageParam.limit,
            };
          },
        },
        // The `query` function receives `{queryArg, pageParam}` as its argument
        query: (args) => {
          const { queryArg, pageParam } = args;

          return {
            url: `/conversations`,
            params: {
              archived:
                typeof queryArg.archived === 'boolean'
                  ? String(queryArg.archived)
                  : queryArg.archived,
              unreadOnly: queryArg.unreadOnly,
              scope: queryArg.scope,
              limit: pageParam.limit,
              skip: pageParam.skip,
            },
          };
        },
        providesTags: ['ConversationList', 'ConversationController'],
      }),
      getUserConversations: build.infiniteQuery<
        GetConversationsApiResponse,
        GetConversationsApiArg & {
          userId: string;
          sort?: string;
          lastMessageFrom?: string;
          lastMessageTo?: string;
          keywords?: string;
          includeDeleted?: boolean | string;
        },
        ConversationsInitialPageParam
      >({
        infiniteQueryOptions: {
          initialPageParam: {
            skip: 0,
            limit: 25,
          },
          getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const nextOffset = lastPageParam.skip + lastPageParam.limit;
            const remainingItems = lastPage?.total
              ? lastPage.total - nextOffset
              : 0;
            if (remainingItems <= 0) return undefined;
            return { ...lastPageParam, skip: nextOffset };
          },
          getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
            const prevOffset = firstPageParam.skip - firstPageParam.limit;
            if (prevOffset < 0) return undefined;
            return { ...firstPageParam, skip: prevOffset } as any;
          },
        },
        query: (args) => {
          const { queryArg, pageParam } = args;
          const {
            userId,
            archived,
            sort = 'lastMessage:desc',
            lastMessageFrom,
            lastMessageTo,
            keywords,
            includeDeleted,
          } = queryArg as any;
          return {
            url: `/users/${userId}/conversations`,
            params: {
              archived:
                typeof archived === 'boolean' ? String(archived) : archived,
              sort,
              limit: pageParam.limit,
              skip: pageParam.skip,
              lastMessageFrom,
              lastMessageTo,
              keywords,
              includeDeleted,
            },
          };
        },
        providesTags: ['ConversationList', 'ConversationController'],
      }),
    }),
  });

export const enhancedApi = extendedApi.enhanceEndpoints({
  endpoints: {
    patchMessagesByIdReadReceipt: {
      onQueryStarted: async (
        args: PatchMessagesByIdApiArg,
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: serverMessage } = await queryFulfilled;
          dispatch(
            ellipsisApi.util.updateQueryData(
              'getConversationsById',
              { id: serverMessage.conversationId },
              (draft: { messages?: MessageWithRelations[] }) => {
                const idx = draft.messages?.findIndex(
                  (m) => m.id === serverMessage.id
                );
                if (idx != null && idx > -1 && draft.messages) {
                  // overwrite the optimistic with server payload, ensure pending=false
                  draft.messages[idx] = {
                    ...serverMessage,
                  } as MessageWithRelations;
                } else if (draft.messages) {
                  // if not found (cache eviction etc.), append as a fallback
                  draft.messages.push({
                    ...serverMessage,
                  } as MessageWithRelations);
                }
              }
            )
          );
          console.log(`Marking message ${serverMessage.id} as read`);
          dispatch(enhancedApi.util.invalidateTags(['ConversationList']));
        } catch (error: any) {
          console.error(
            'Caught error in patchMessagesByIdReadReceipt, error:',
            error.message
          );
        }
      },
    },
    patchMessagesById: {
      onQueryStarted: async (
        args: PatchMessagesByIdApiArg,
        { dispatch, queryFulfilled }
      ) => {
        let updateQueryDataResult;

        // Transform read receipts in the message partial if they exist
        if (args.messagePartial.readReceipts) {
          args.messagePartial = transformReadReceipts(args.messagePartial);
        }

        try {
          updateQueryDataResult = dispatch(
            ellipsisApi.util.updateQueryData(
              'getConversationsById',
              { id: args.messagePartial.conversationId ?? '' },
              (draft: ConversationWithRelations) => {
                const messageIndex =
                  draft.messages?.findIndex((m) => m.id === args.id) ?? -1;
                if (messageIndex !== -1 && draft.messages) {
                  draft.messages[messageIndex] = transformReadReceipts({
                    ...draft.messages[messageIndex],
                    ...args.messagePartial,
                  });
                } else if (draft.messages) {
                  draft.messages = [
                    ...(draft.messages || []),
                    transformReadReceipts({
                      id: args.id,
                      ...args.messagePartial,
                    }) as Message,
                  ];
                }
              }
            ) as unknown as AnyAction
          );
          await queryFulfilled;

          const conversationId = args.messagePartial.conversationId;

          if (conversationId) {
            dispatch(
              extendedApi.util.updateQueryData(
                'getConversations',
                { archived: 'false', limit: 10, skip: 0 },
                (draft) => {
                  const conversation: ConversationWithRelations | undefined =
                    draft.pages.map((page) =>
                      page.conversations?.find((c) => c.id === conversationId)
                    );
                  if (conversation) {
                    // Find the message in the conversation's messages array
                    const messageIndex =
                      conversation.messages?.findIndex(
                        (m: MessageWithRelations) => m._id === args.id
                      ) ?? -1;

                    if (messageIndex !== -1 && conversation.messages) {
                      // Update the existing message
                      conversation.messages[messageIndex] =
                        transformReadReceipts({
                          ...conversation.messages[messageIndex],
                          ...args.messagePartial,
                        });
                    } else if (conversation.messages) {
                      // Add the message if it doesn't exist
                      conversation.messages = [
                        ...(conversation.messages || []),
                        transformReadReceipts({
                          id: args.id,
                          ...args.messagePartial,
                          conversationId,
                        }) as MessageWithRelations,
                      ];
                    }
                  }
                }
              ) as unknown as AnyAction
            );
          }
        } catch (error: any) {
          if (updateQueryDataResult) updateQueryDataResult.undo();
          toast.error('Failed to send message.');
          console.error(
            'Caught error in patchMessageById, error:',
            error.message
          );
        }
      },
    },
    postMessages: {
      onQueryStarted: async (
        args: PostMessagesApiArg,
        { dispatch, queryFulfilled }
      ) => {
        let updateQueryDataResult;
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const optimisticMessage: MessageWithRelations = {
          ...args.newMessage,
          id: tempId,
          tempId,
          pending: true as any,
        } as MessageWithRelations;

        try {
          updateQueryDataResult = dispatch(
            ellipsisApi.util.updateQueryData(
              'getConversationsById',
              { id: args.newMessage.conversationId },
              (draft: ConversationWithRelations) => {
                draft.messages = [...(draft.messages || []), optimisticMessage];
              }
            ) as unknown as AnyAction
          );
          const { data: serverMessage } = await queryFulfilled;
          dispatch(
            ellipsisApi.util.updateQueryData(
              'getConversationsById',
              { id: args.newMessage.conversationId },
              (draft: { messages?: MessageWithRelations[] }) => {
                // Prefer matching by tempId (stable across optimistic -> confirmed)
                let idx =
                  draft.messages?.findIndex(
                    (m) => (m as any).tempId === tempId
                  ) ?? -1;
                if (idx === -1) {
                  idx = draft.messages?.findIndex((m) => m.id === tempId) ?? -1;
                }
                // If the socket already delivered the real message, avoid creating a duplicate
                if (idx === -1 && serverMessage.id) {
                  idx =
                    draft.messages?.findIndex(
                      (m) => m.id === serverMessage.id
                    ) ?? -1;
                }
                if (idx != null && idx > -1 && draft.messages) {
                  // overwrite the optimistic with server payload, preserving tempId so
                  // list itemKey stays stable and socket reconciliation can still match
                  draft.messages[idx] = {
                    ...serverMessage,
                    tempId,
                    pending: false as any,
                  } as MessageWithRelations;
                } else if (draft.messages) {
                  // if not found (cache eviction etc.), append as a fallback
                  draft.messages.push({
                    ...serverMessage,
                    tempId,
                    pending: false as any,
                  } as MessageWithRelations);
                }
              }
            )
          );
          toast.info('Message sent successfully.');
        } catch (error: any) {
          if (updateQueryDataResult) updateQueryDataResult.undo();
          toast.error('Failed to send message.');
          console.error(error);
        }
      },
    },
    getConversationsById: {
      // Add a keepUnusedDataFor option to ensure the cache is kept for a reasonable time
      keepUnusedDataFor: 300, // Keep data in cache for 5 minutes
      transformResponse: (response: any) => {
        if (response?.messages?.length) {
          // Transform read receipts in all messages
          return {
            ...response,
            messages: response.messages.map((message: any) =>
              transformReadReceipts(message)
            ),
          };
        }
        return response;
      },
    },
    getConversations: {
      transformResponse: (response: any) => {
        const transformConversation = (conversation: any) => {
          if (conversation?.messages?.length) {
            return {
              ...conversation,
              messages: conversation.messages.map((message: any) =>
                transformReadReceipts(message)
              ),
            };
          }
          return conversation;
        };

        // Handle both the old and new response formats
        if (Array.isArray(response)) {
          // Old format: array of conversations
          console.log('Old format detected, transforming...');
          return {
            conversations: response.map(transformConversation),
            total: response.length,
          } as GetConversationsApiResponse;
        } else {
          // New format: object with conversations and total
          return {
            ...response,
            conversations:
              response.conversations?.map(transformConversation) || [],
            total: response.total || 0,
          } as GetConversationsApiResponse;
        }
      },
    },
    patchConversationsById: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Only handle optimistic logic when toggling archived
        const hasArchivedField =
          args?.conversationPartial &&
          Object.prototype.hasOwnProperty.call(
            args.conversationPartial,
            'archived'
          );
        if (!hasArchivedField) {
          try {
            await queryFulfilled;
          } catch (error) {
            console.error('Failed to update conversation:', error);
            // no-op
          }
          return;
        }

        const isArchiving = !!args.conversationPartial.archived;
        const conversationId = args.id as string;

        type Undoable = { undo: () => void } | undefined;
        const undos: Undoable[] = [];

        const runUpdate = (
          params: {
            archived: boolean | string;
            unreadOnly?: boolean;
            scope?: 'mine' | 'all';
            limit?: number;
            skip?: number;
          },
          updater: (draft: any) => void
        ) => {
          try {
            const res = dispatch(
              extendedApi.util.updateQueryData(
                'getConversations',
                params as any,
                updater
              ) as unknown as AnyAction
            ) as any;
            undos.push(res);
          } catch (error: any) {
            console.error('Failed to update conversation:', error);
          }
        };

        // Helper to remove a conversation by id from pages
        const removeFromPages = (draft: any) => {
          draft.pages?.forEach((page: any) => {
            if (!Array.isArray(page.conversations)) return;
            const idx = page.conversations.findIndex(
              (c: any) => c.id === conversationId
            );
            if (idx > -1) {
              page.conversations.splice(idx, 1);
              if (typeof page.total === 'number') {
                page.total = Math.max(0, page.total - 1);
              }
            }
          });
        };

        // Helper to insert into the first page
        const insertIntoFirstPage = (draft: any, conversationPatch: any) => {
          if (!draft.pages?.length) return;
          const first = draft.pages[0];
          first.conversations = first.conversations || [];
          // Prevent duplicates
          if (!first.conversations.find((c: any) => c.id === conversationId)) {
            first.conversations.unshift({
              id: conversationId,
              ...conversationPatch,
            });
            if (typeof first.total === 'number') first.total = first.total + 1;
          }
        };

        // Update by-id cache optimistically
        let byIdUndo: Undoable;
        try {
          const res = dispatch(
            ellipsisApi.util.updateQueryData(
              'getConversationsById',
              { id: conversationId },
              (draft: any) => {
                if (draft) {
                  draft.archived = isArchiving;
                }
              }
            ) as unknown as AnyAction
          ) as any;
          byIdUndo = res;
        } catch (error: any) {
          console.error('Failed to update conversation:', error);
        }

        // Remove from active/unread lists when archiving; or from archived when unarchiving
        const scopes: Array<'mine' | 'all'> = ['mine', 'all'];
        const bools: boolean[] = [true, false];

        if (isArchiving) {
          scopes.forEach((scope) => {
            // Remove from active + unread variants
            bools.forEach((unreadOnly) => {
              runUpdate(
                { archived: 'false', unreadOnly, scope, limit: 10, skip: 0 },
                (draft) => removeFromPages(draft)
              );
            });
            // Insert into the archived list top of page 1
            runUpdate(
              {
                archived: 'true',
                unreadOnly: false,
                scope,
                limit: 10,
                skip: 0,
              },
              (draft) => insertIntoFirstPage(draft, { archived: true })
            );
          });
        } else {
          scopes.forEach((scope) => {
            // Remove from archived
            runUpdate(
              {
                archived: 'true',
                unreadOnly: false,
                scope,
                limit: 10,
                skip: 0,
              },
              (draft) => removeFromPages(draft)
            );
            // Insert into the active list
            runUpdate(
              {
                archived: 'false',
                unreadOnly: false,
                scope,
                limit: 10,
                skip: 0,
              },
              (draft) => insertIntoFirstPage(draft, { archived: false })
            );
          });
        }

        try {
          await queryFulfilled;
          // Invalidate list totals so counts refresh
          dispatch(enhancedApi.util.invalidateTags(['ConversationList']));
        } catch (error: any) {
          // Rollback all
          undos.reverse().forEach((u) => u && u.undo());
          if (byIdUndo) byIdUndo.undo();

          if (error?.error?.status === 403 || error?.status === 403) {
            toast.error('You don’t have permission to perform this action');
          } else {
            toast.error('Failed to update conversation.');
          }
        }
      },
    },
    deleteMessagesById: {
      onQueryStarted: async (
        args: DeleteMessagesByIdApiArg,
        { dispatch, queryFulfilled }
      ) => {
        try {
          await queryFulfilled;
          toast.success('Message deleted successfully.');
          // After a message is deleted, we must refresh the conversation data.
          dispatch(
            enhancedApi.util.invalidateTags([
              'ConversationList',
              'ConversationController',
            ])
          );
        } catch (error: any) {
          toast.error('Failed to delete message.');
          console.error(
            'Caught error in deleteMessagesById, error:',
            error.message
          );
        }
      },
    },
  },
});

export const {
  useGetConversationsByIdQuery,
  useGetConversationsInfiniteQuery,
  usePatchConversationsByIdMutation,
  useDeleteMessagesByIdMutation,
} = enhancedApi;

export * from './ellipsisApi';
